import { fetchExperienceCategories, fetchExperienceListFacets } from 'actions/ExperienceActions'
import { EXP_CATEGORY_LUX_PLUS_ID } from 'constants/experience'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { arrayToObject, groupBy, sortBy, sum } from 'lib/array/arrayUtils'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useEffect, useMemo } from 'react'

export interface CategoryData {
  parent: App.ExperienceItemCategory
  children: Array<App.ExperienceItemCategory>
  count: number
}

function useMostPopulatedSearchExperienceFacets(place?: App.Place): {
  primaryCategories: Array<CategoryData>,
  categories: Array<App.ExperienceCategoryFacet>,
  collections: Array<App.ExperienceCollectionFacet>,
  priceRange: App.ExperienceFacets['price'],
  fetching: boolean,
} {
  const dispatch = useAppDispatch()
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const searchFacets = useAppSelector(state => state.experience.experienceListFacets)
  const isLuxPlusEnabled = useAppSelector(state => getIsEnabledLuxPlusDiscountedExperiences(state))
  const experiencesCollectionsEnabled = useOptimizelyExperiment(OptimizelyExperiments.experiencesCollections)

  useEffect(() => {
    if (!place) {
      return
    }

    dispatch(fetchExperienceListFacets(place.id))
    dispatch(fetchExperienceCategories())
    // eslint-disable-next-line
  }, [place])

  const categoriesFacets = useMemo<Array<App.ExperienceCategoryFacet>>(() => {
    if (!place) {
      return []
    }

    return searchFacets[place.id]?.facets?.category ?? []
  }, [place, searchFacets])

  const primaryCategories = useMemo<Array<CategoryData>>(() => {
    if (categoriesFacets.length == 0) {
      return []
    }

    // prepare reference data for access in calculations below
    const childToParentCategories = groupBy(categories.filter(cat => cat.parentId), cat => cat.parentId)
    const categoryFacetsById = arrayToObject(
      categoriesFacets,
      (cat) => cat.categoryId,
    )

    // we show a tile for each 'parent' category
    const topLevelCategories = categories.filter(cat => cat.level === 'parent')
    // new search facets doesn't return a count at the parent level, so we have to manually
    // add up all child facets to get an accurate count
    const topLevelCategoryFacets = topLevelCategories.map(cat => ({
      cat,
      enabled: cat.id !== EXP_CATEGORY_LUX_PLUS_ID || isLuxPlusEnabled,
      count: sum(childToParentCategories.get(cat.id) ?? [], childCat => categoryFacetsById[childCat.id]?.count ?? 0),
    }))

    const sortedCategories = sortBy(topLevelCategoryFacets, val => val.count, 'desc')

    return sortedCategories.filter(fac => fac.count !== 0 && fac.enabled).map(fac => ({
      parent: fac.cat,
      children: childToParentCategories.get(fac.cat.id) ?? [],
      count: fac.count,
    }))
  }, [categories, categoriesFacets, isLuxPlusEnabled])

  const collections = useMemo<Array<App.ExperienceCollectionFacet>>(() => {
    if (!place || !experiencesCollectionsEnabled) {
      return []
    }

    return searchFacets[place.id]?.facets?.collection ?? []
  }, [place, searchFacets, experiencesCollectionsEnabled])

  const priceRange = useMemo<App.ExperienceFacets['price']>(() => {
    if (!place) {
      return { min: 0, max: 0 }
    }

    return searchFacets[place.id]?.facets?.price ?? { min: 0, max: 0 }
  }, [searchFacets, place])

  const fetching = searchFacets[place?.id!]?.fetching ?? true

  return { primaryCategories, categories: categoriesFacets, collections, priceRange, fetching }
}

export default useMostPopulatedSearchExperienceFacets
